import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatDate, prettify } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, RefreshCw, Download, Trash2, UserCheck, UserX, Search } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Subscribers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [nw, setNw] = useState({ email: "", name: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {}; if (status !== "all") params.status = status; if (q) params.q = q;
      const { data } = await api.get("/marketing/subscribers", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [status, q]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/marketing/subscribers/sync"); toast.success(`Synced — ${data.created} new subscriber(s) added`); load(); }
    catch { toast.error("Sync failed"); } finally { setSyncing(false); }
  };
  const add = async () => {
    if (!nw.email.trim()) return toast.error("Email is required");
    try { await api.post("/marketing/subscribers", nw); toast.success("Subscriber added"); setAddOpen(false); setNw({ email: "", name: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  const toggle = async (s) => {
    const next = s.status === "subscribed" ? "unsubscribed" : "subscribed";
    await api.put(`/marketing/subscribers/${s.id}`, { status: next }); load();
  };
  const remove = async (s) => { await api.delete(`/marketing/subscribers/${s.id}`); toast.success("Removed"); load(); };
  const exportCsv = async () => {
    try {
      const res = await api.get("/marketing/subscribers/export", { params: status !== "all" ? { status } : {}, responseType: "blob" });
      const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = "subscribers.csv"; a.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-5" data-testid="marketing-subscribers">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#252221]">Subscribers</h1>
          <p className="text-sm text-muted-foreground">Your newsletter audience, built from your CRM and public signups.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={sync} disabled={syncing} data-testid="sync-subscribers-button"><RefreshCw size={15} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} /> Sync from CRM</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-subscribers-button"><Download size={15} className="mr-1.5" /> Export</Button>
          <Button onClick={() => setAddOpen(true)} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="add-subscriber-button"><Plus size={15} className="mr-1.5" /> Add</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name" className="pl-8 w-[240px]" data-testid="subscribers-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px]" data-testid="subscribers-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="subscribed">Subscribed</SelectItem><SelectItem value="unsubscribed">Unsubscribed</SelectItem></SelectContent>
        </Select>
      </div>

      <Card className="border-[#E6EDF2] overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        {loading ? <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div> :
          items.length === 0 ? <div className="p-10 text-center text-muted-foreground" data-testid="subscribers-empty">No subscribers yet. Use “Sync from CRM” to import contacts.</div> : (
          <div className="overflow-x-auto mmm-scroll">
            <Table data-testid="subscribers-table">
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead className="text-xs font-semibold text-[#1C4358]/80">Email</TableHead>
                <TableHead className="text-xs font-semibold text-[#1C4358]/80">Name</TableHead>
                <TableHead className="text-xs font-semibold text-[#1C4358]/80">Source</TableHead>
                <TableHead className="text-xs font-semibold text-[#1C4358]/80">Status</TableHead>
                <TableHead className="text-xs font-semibold text-[#1C4358]/80">Added</TableHead>
                <TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id} className="hover:bg-[rgba(155,185,206,0.10)]" data-testid="subscriber-row">
                    <TableCell className="font-medium text-[#252221]">{s.email}</TableCell>
                    <TableCell className="text-sm">{s.name || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{prettify(s.source || "")}</TableCell>
                    <TableCell><StatusBadge value={s.status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(s.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => toggle(s)} className="p-2 rounded-lg text-[#1C4358] hover:bg-[rgba(28,67,88,0.08)]" title={s.status === "subscribed" ? "Unsubscribe" : "Resubscribe"} data-testid="subscriber-toggle">{s.status === "subscribed" ? <UserX size={15} /> : <UserCheck size={15} />}</button>
                        <button onClick={() => remove(s)} className="p-2 rounded-lg text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]" data-testid="subscriber-delete" aria-label="Delete"><Trash2 size={15} /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-subscriber-dialog">
          <DialogHeader><DialogTitle>Add subscriber</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">Email</Label><Input value={nw.email} onChange={(e) => setNw({ ...nw, email: e.target.value })} placeholder="name@example.com" data-testid="subscriber-email-input" /></div>
            <div><Label className="text-sm">Name (optional)</Label><Input value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} data-testid="subscriber-name-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button onClick={add} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="subscriber-save-button">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
